import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Star, Trash2, Loader, Image as ImageIcon, Calendar } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { toggleFavorite, deleteRecipe } from '../utils/recipeService';
import BottomNav from '../components/BottomNav';

export default function RecipeDetail() {
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScreenshot, setShowScreenshot] = useState(false);
  
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();

  useEffect(() => {
    loadRecipe();
  }, [id]);

  async function loadRecipe() {
    try {
      setLoading(true);
      const recipeDoc = await getDoc(doc(db, 'recipes', id));
      
      if (recipeDoc.exists()) {
        const data = recipeDoc.data();
        if (data.userId === currentUser.uid) {
          setRecipe({ id: recipeDoc.id, ...data });
        } else {
          navigate('/library');
        }
      } else {
        navigate('/library');
      }
    } catch (error) {
      console.error('Error loading recipe:', error);
      navigate('/library');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleFavorite() {
    try {
      await toggleFavorite(recipe.id, recipe.isFavorite);
      setRecipe({ ...recipe, isFavorite: !recipe.isFavorite });
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this recipe? This cannot be undone.')) return;

    try {
      await deleteRecipe(recipe.id);
      navigate('/library');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      alert('Failed to delete recipe. Please try again.');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f8f0f5 0%, #e8e4f3 100%)' }}>
        <Loader size={40} className="text-purple-600 animate-spin" />
      </div>
    );
  }

  if (!recipe) {
    console.log('Recipe is null/undefined');
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f8f0f5 0%, #e8e4f3 100%)' }}>
        <div className="text-center">
          <p className="text-gray-800 mb-4">Recipe not found</p>
          <button onClick={() => navigate('/library')} className="btn-primary">
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  console.log('Recipe loaded:', recipe);

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(135deg, #f8f0f5 0%, #e8e4f3 100%)' }}>
      {/* Flowing Blob Header */}
      <div className="relative h-72 overflow-hidden mb-6">
        <div 
          className="absolute w-80 h-80 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
            top: '-60px',
            left: '-60px',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 8s ease-in-out infinite',
          }}
        />
        
        <div 
          className="absolute w-64 h-64 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #fb7185 0%, #fda4af 100%)',
            top: '60px',
            right: '-80px',
            borderRadius: '60% 40% 30% 70% / 50% 60% 40% 50%',
            animation: 'morph 10s ease-in-out infinite reverse',
          }}
        />
        
        <div 
          className="absolute w-56 h-56 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #ffe082 0%, #fff59d 100%)',
            bottom: '-40px',
            left: '90px',
            opacity: 0.85,
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 12s ease-in-out infinite',
          }}
        />
        
        <div className="relative z-10 max-w-md mx-auto px-6 pt-8">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => navigate('/library')} 
              className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              <ChevronLeft size={24} className="text-white" />
            </button>
            
            <div className="flex gap-2">
              <button
                onClick={handleToggleFavorite}
                className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
                style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              >
                <Star
                  size={24}
                  className={recipe.isFavorite ? 'text-yellow-300 fill-yellow-300' : 'text-white'}
                />
              </button>
              
              <button
                onClick={handleDelete}
                className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
                style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              >
                <Trash2 size={24} className="text-white" />
              </button>
            </div>
          </div>

          <h1 
            className="text-white text-3xl font-bold mb-3"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)' }}
          >
            {recipe.name}
          </h1>
          
          <p 
            className="text-white/95 text-sm"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}
          >
            {recipe.ingredients?.length || 0} ingredients
            {recipe.source && ` • ${recipe.source}`}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-6 pt-6 space-y-4">
        {/* Screenshot Card */}
        {recipe.imageUrl && (
          <div className="bg-white rounded-3xl overflow-hidden shadow-md">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2 text-gray-700">
                <ImageIcon size={18} />
                <span className="font-semibold text-sm">Recipe Photo</span>
              </div>
            </div>
            <div 
              className="h-56 bg-cover bg-center cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundImage: `url(${recipe.imageUrl})` }}
              onClick={() => setShowScreenshot(true)}
            />
          </div>
        )}

        {/* Ingredients Card */}
        <div className="bg-white rounded-3xl p-6 shadow-md">
          <h3 className="font-bold text-xl text-purple-600 mb-4">
            Ingredients ({recipe.ingredients?.length || 0})
          </h3>
          <ul className="space-y-3">
            {recipe.ingredients?.map((ingredient, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-purple-400 mt-1">•</span>
                <span className="text-gray-700 flex-1">{ingredient}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Instructions Card */}
        {recipe.instructions && Array.isArray(recipe.instructions) && recipe.instructions.length > 0 && (
          <div className="bg-white rounded-3xl p-6 shadow-md">
            <h3 className="font-bold text-xl text-purple-600 mb-4">Instructions</h3>
            <ol className="space-y-4">
              {recipe.instructions.map((step, index) => (
                <li key={index} className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </span>
                  <p className="text-gray-700 flex-1 pt-1">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Add to Calendar Button */}
        <button
          onClick={() => navigate('/calendar')}
          className="btn-primary w-full"
        >
          <Calendar size={20} />
          Add to Meal Plan
        </button>

        {/* Tags */}
        {recipe.tags && recipe.tags.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {recipe.tags.map((tag, index) => (
              <span key={index} className="tag-pink">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Screenshot Modal */}
      {showScreenshot && recipe.imageUrl && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowScreenshot(false)}
        >
          <div className="max-w-4xl w-full">
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowScreenshot(false)}
                className="text-white bg-white/20 backdrop-blur-sm p-3 rounded-xl hover:bg-white/30"
              >
                Close
              </button>
            </div>
            <img 
              src={recipe.imageUrl} 
              alt={recipe.name}
              className="w-full rounded-2xl"
            />
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
